import type { IntegerAmount } from '@actual-app/core/shared/util';

/**
 * «Pagas extra y calendario de nómina»: cálculo puro (sin React) de la
 * configuración de la nómina de la persona (12, 14, 15 pagas o prorrateadas),
 * las fechas de cada paga extra, el «sueldo de verdad» (lo del año entre 12)
 * y el plan de qué hará cada paga. Todo en céntimos.
 *
 * Se guarda en dos prefs sincronizadas del presupuesto:
 * - `nomina`: la configuración (`ConfigNomina`, JSON).
 * - `pagas-plan`: el plan y el estado de cada paga de cada año
 *   (`PlanesPagas`, JSON con claves «idPaga@AAAA»).
 */

export type TipoPagas = '12' | '14' | '15' | 'prorrateadas';

/** Día de cobro: 1..31 (se recorta al último día del mes) o el último. */
export type DiaCobro = number | 'ultimo';

export type PagaExtra = {
  id: string;
  /** Nombre propio («Verano», «Navidad», «Beneficios»…); vacío = por mes. */
  nombre: string;
  /** Mes en que se cobra, 1..12. */
  mes: number;
  dia: DiaCobro;
  /** Neto de la paga; `null` = igual que la nómina. */
  importe: IntegerAmount | null;
};

export type ConfigNomina = {
  tipo: TipoPagas;
  /** Nómina neta de un mes normal. */
  neta: IntegerAmount;
  dia: DiaCobro;
  /** Cuenta en la que se cobra; `null` = sin elegir. */
  cuentaId: string | null;
  pagas: PagaExtra[];
  /** Gavi avisa un mes antes de cada paga para planearla. */
  avisar: boolean;
  /**
   * Deducida del historial y confirmada con un toque: se pone al día sola
   * con lo que se vaya cobrando. Al tocar algo a mano deja de serlo.
   */
  auto: boolean;
};

export const CONFIG_VACIA: ConfigNomina = {
  tipo: '12',
  neta: 0,
  dia: 'ultimo',
  cuentaId: null,
  pagas: [],
  avisar: true,
  auto: false,
};

/** Pagas extra de serie para cada tipo (las habituales en España). */
export function pagasPorDefecto(tipo: TipoPagas): PagaExtra[] {
  const verano: PagaExtra = {
    id: 'verano',
    nombre: '',
    mes: 6,
    dia: 'ultimo',
    importe: null,
  };
  const navidad: PagaExtra = {
    id: 'navidad',
    nombre: '',
    mes: 12,
    dia: 20,
    importe: null,
  };
  const beneficios: PagaExtra = {
    id: 'beneficios',
    nombre: '',
    mes: 3,
    dia: 'ultimo',
    importe: null,
  };
  switch (tipo) {
    case '14':
      return [verano, navidad];
    case '15':
      return [beneficios, verano, navidad];
    default:
      return [];
  }
}

/** Cambia el tipo y deja las pagas de serie de ese tipo. */
export function conTipo(config: ConfigNomina, tipo: TipoPagas): ConfigNomina {
  return { ...config, tipo, pagas: pagasPorDefecto(tipo) };
}

function esEntero(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v);
}

function leerDia(v: unknown, reserva: DiaCobro): DiaCobro {
  if (v === 'ultimo') {
    return 'ultimo';
  }
  if (esEntero(v) && v >= 1 && v <= 31) {
    return v;
  }
  return reserva;
}

function leerPaga(v: unknown): PagaExtra | null {
  if (!v || typeof v !== 'object') {
    return null;
  }
  const p = v as Record<string, unknown>;
  if (typeof p.id !== 'string' || !p.id) {
    return null;
  }
  if (!esEntero(p.mes) || p.mes < 1 || p.mes > 12) {
    return null;
  }
  return {
    id: p.id,
    nombre: typeof p.nombre === 'string' ? p.nombre.slice(0, 40) : '',
    mes: p.mes,
    dia: leerDia(p.dia, 'ultimo'),
    importe: esEntero(p.importe) && p.importe >= 0 ? p.importe : null,
  };
}

/** Lee la pref `nomina`; cualquier cosa rara vuelve a `null` (sin configurar). */
export function parseConfig(
  raw: string | null | undefined,
): ConfigNomina | null {
  if (!raw) {
    return null;
  }
  let datos: unknown;
  try {
    datos = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!datos || typeof datos !== 'object') {
    return null;
  }
  const d = datos as Record<string, unknown>;
  const tipo: TipoPagas =
    d.tipo === '14' || d.tipo === '15' || d.tipo === 'prorrateadas'
      ? d.tipo
      : '12';
  const pagas = Array.isArray(d.pagas)
    ? d.pagas.map(leerPaga).filter((p): p is PagaExtra => p !== null)
    : [];
  return {
    tipo,
    neta: esEntero(d.neta) && d.neta >= 0 ? d.neta : 0,
    dia: leerDia(d.dia, 'ultimo'),
    cuentaId: typeof d.cuentaId === 'string' ? d.cuentaId : null,
    // Sin pagas extra en 12 y prorrateadas, aunque quedara alguna guardada.
    pagas: tipo === '12' || tipo === 'prorrateadas' ? [] : pagas,
    avisar: d.avisar !== false,
    auto: d.auto === true,
  };
}

/** Neto de una paga extra (si no tiene importe propio, el de la nómina). */
export function importePaga(config: ConfigNomina, paga: PagaExtra) {
  return paga.importe ?? config.neta;
}

/** Lo que se cobra en el año: 12 nóminas más las pagas extra. */
export function totalAnual(config: ConfigNomina): IntegerAmount {
  return (
    config.neta * 12 +
    config.pagas.reduce((suma, p) => suma + importePaga(config, p), 0)
  );
}

/** «Sueldo de verdad»: lo del año repartido entre 12 meses (redondeado). */
export function sueldoDeVerdad(config: ConfigNomina): IntegerAmount {
  return Math.round(totalAnual(config) / 12);
}

function diasDelMes(ano: number, mes: number) {
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}

function dos(n: number) {
  return String(n).padStart(2, '0');
}

/** Fecha `AAAA-MM-DD` de un cobro en un mes (día recortado al del mes). */
export function fechaCobro(ano: number, mes: number, dia: DiaCobro): string {
  const ultimo = diasDelMes(ano, mes);
  const d = dia === 'ultimo' ? ultimo : Math.min(dia, ultimo);
  return `${ano}-${dos(mes)}-${dos(d)}`;
}

/** Días naturales de `desde` a `hasta` (`AAAA-MM-DD`); negativo si ya pasó. */
export function diasEntre(desde: string, hasta: string): number {
  const a = Date.UTC(
    +desde.slice(0, 4),
    +desde.slice(5, 7) - 1,
    +desde.slice(8, 10),
  );
  const b = Date.UTC(
    +hasta.slice(0, 4),
    +hasta.slice(5, 7) - 1,
    +hasta.slice(8, 10),
  );
  return Math.round((b - a) / 86_400_000);
}

export type CobroPaga = {
  paga: PagaExtra;
  /** Clave del plan: «idPaga@AAAA». */
  clave: string;
  fecha: string;
  importe: IntegerAmount;
  /** Días desde hoy (0 = hoy; negativo = ya pasó). */
  dias: number;
};

export function clavePaga(idPaga: string, ano: number) {
  return `${idPaga}@${ano}`;
}

/**
 * Cobros de paga extra alrededor de `hoy`: los del año pasado, este y el
 * siguiente, ordenados por fecha. Sirve para la próxima y para la última.
 */
export function cobrosPagas(config: ConfigNomina, hoy: string): CobroPaga[] {
  const ano = +hoy.slice(0, 4);
  const cobros: CobroPaga[] = [];
  for (const a of [ano - 1, ano, ano + 1]) {
    for (const paga of config.pagas) {
      const fecha = fechaCobro(a, paga.mes, paga.dia);
      cobros.push({
        paga,
        clave: clavePaga(paga.id, a),
        fecha,
        importe: importePaga(config, paga),
        dias: diasEntre(hoy, fecha),
      });
    }
  }
  cobros.sort((x, y) => (x.fecha < y.fecha ? -1 : x.fecha > y.fecha ? 1 : 0));
  return cobros;
}

/** La próxima paga extra (hoy incluido), o `null` si no hay. */
export function proximaPaga(
  config: ConfigNomina,
  hoy: string,
): CobroPaga | null {
  return cobrosPagas(config, hoy).find(c => c.dias >= 0) ?? null;
}

/**
 * La paga que «acaba de llegar»: cobrada hace como mucho `ventana` días (de
 * serie, 15). Es la que se ofrece repartir el día de cobro.
 */
export function pagaRecienCobrada(
  config: ConfigNomina,
  hoy: string,
  ventana = 15,
): CobroPaga | null {
  const recientes = cobrosPagas(config, hoy).filter(
    c => c.dias <= 0 && c.dias >= -ventana,
  );
  return recientes.at(-1) ?? null;
}

/** Lo que tiene que pasar de la nómina para dar la paga por llegada. */
const MARGEN_PAGA = 0.1;
const MARGEN_PAGA_MINIMO = 3000;

/**
 * Lo que llegó de paga extra en un mes: lo cobrado por encima de la nómina,
 * si pasa de ella al menos un 10 % (30 € como mínimo). `null` = aún no ha
 * llegado (o no se sabe). Mira el dinero de verdad, no la fecha prevista:
 * las pagas se adelantan y cambian de un año a otro.
 */
export function extraCobrada(
  config: ConfigNomina,
  ingresosMes: IntegerAmount | null,
): IntegerAmount | null {
  if (ingresosMes == null || config.neta <= 0) {
    return null;
  }
  const extra = ingresosMes - config.neta;
  return extra >= Math.max(config.neta * MARGEN_PAGA, MARGEN_PAGA_MINIMO)
    ? extra
    : null;
}

/**
 * La paga extra de este mes, si la hay: con lo que llegó de verdad cuando ya
 * se cobró (aunque sea antes del día previsto) o con lo previsto si no.
 */
export function pagaDelMes(
  config: ConfigNomina,
  hoy: string,
  ingresosMes: IntegerAmount | null,
): { cobro: CobroPaga; llego: boolean } | null {
  const mes = hoy.slice(0, 7);
  const cobro = cobrosPagas(config, hoy).find(c => c.fecha.startsWith(mes));
  if (!cobro) {
    return null;
  }
  const extra = extraCobrada(config, ingresosMes);
  return extra == null
    ? { cobro, llego: false }
    : { cobro: { ...cobro, importe: extra }, llego: true };
}

/**
 * La próxima paga extra que falta por cobrar: la de este mes no cuenta si ya
 * llegó, aunque su fecha prevista sea hoy o más adelante.
 */
export function proximaPorCobrar(
  config: ConfigNomina,
  hoy: string,
  ingresosMes: IntegerAmount | null,
): CobroPaga | null {
  const delMes = pagaDelMes(config, hoy, ingresosMes);
  return (
    cobrosPagas(config, hoy).find(
      c => c.dias >= 0 && !(delMes?.llego && c.clave === delMes.cobro.clave),
    ) ?? null
  );
}

export type MesCalendario = {
  /** `AAAA-MM` */
  mes: string;
  nomina: IntegerAmount;
  extras: { paga: PagaExtra; fecha: string; importe: IntegerAmount }[];
  total: IntegerAmount;
};

/** Los 12 meses de un año con la nómina y las pagas extra de cada uno. */
export function calendarioAno(
  config: ConfigNomina,
  ano: number,
): MesCalendario[] {
  return Array.from({ length: 12 }, (_, i) => {
    const mes = i + 1;
    const extras = config.pagas
      .filter(p => p.mes === mes)
      .map(paga => ({
        paga,
        fecha: fechaCobro(ano, mes, paga.dia),
        importe: importePaga(config, paga),
      }));
    const total = config.neta + extras.reduce((s, e) => s + e.importe, 0);
    return { mes: `${ano}-${dos(mes)}`, nomina: config.neta, extras, total };
  });
}

// --- Deducir la nómina del historial ----------------------------------------

/** Un ingreso del historial (céntimos, `AAAA-MM-DD`). */
export type IngresoHistorial = {
  date: string;
  amount: IntegerAmount;
  payee: string | null;
};

/** Lo que Rumbo cree que cobra la persona, para confirmarlo con un toque. */
export type NominaDeducida = Pick<
  ConfigNomina,
  'tipo' | 'neta' | 'dia' | 'pagas'
> & { pagador: string };

function mediana(valores: readonly number[]): number {
  const orden = [...valores].sort((a, b) => a - b);
  const mitad = Math.floor(orden.length / 2);
  return orden.length % 2
    ? orden[mitad]
    : Math.round((orden[mitad - 1] + orden[mitad]) / 2);
}

/** Céntimos redondeados al euro (una nómina «de 1.850 €», no «1.849,73»). */
function alEuro(centimos: number): IntegerAmount {
  return Math.round(centimos / 100) * 100;
}

function idPaga(mes: number): string {
  if (mes === 6 || mes === 7) {
    return 'verano';
  }
  if (mes === 12) {
    return 'navidad';
  }
  return `extra-${mes}`;
}

const NOMINA_MINIMA = 30000;

/**
 * Deduce la nómina de los ingresos de los últimos meses: el pagador que
 * ingresa casi todos los meses (al menos 6) y más dinero en total. La nómina
 * es la mediana de los meses normales (los 3 más recientes, por si subió), el
 * día de cobro el más habitual (del 28 en adelante, «último día») y las pagas
 * extra los meses que cobra 1,6 veces o más lo normal. Sin pagador claro o
 * con ingresos pequeños (menos de 300 €) devuelve `null`.
 */
export function deducirNomina(
  ingresos: readonly IngresoHistorial[],
): NominaDeducida | null {
  type Mes = { suma: number; filas: IngresoHistorial[] };
  const porPagador = new Map<string, Map<string, Mes>>();
  for (const f of ingresos) {
    if (!f.payee || f.amount <= 0) {
      continue;
    }
    const meses = porPagador.get(f.payee) ?? new Map<string, Mes>();
    const mes = f.date.slice(0, 7);
    const actual = meses.get(mes) ?? { suma: 0, filas: [] };
    actual.suma += f.amount;
    actual.filas.push(f);
    meses.set(mes, actual);
    porPagador.set(f.payee, meses);
  }

  let elegido: [string, Map<string, Mes>] | null = null;
  let totalElegido = 0;
  for (const [pagador, meses] of porPagador) {
    if (meses.size < 6) {
      continue;
    }
    const total = [...meses.values()].reduce((s, m) => s + m.suma, 0);
    if (total > totalElegido) {
      elegido = [pagador, meses];
      totalElegido = total;
    }
  }
  if (!elegido) {
    return null;
  }
  const [pagador, meses] = elegido;
  const lista = [...meses].sort(([a], [b]) => a.localeCompare(b));
  const base = mediana(lista.map(([, m]) => m.suma));
  if (base < NOMINA_MINIMA) {
    return null;
  }
  const extras = lista.filter(([, m]) => m.suma >= base * 1.6);
  const normales = lista.filter(([, m]) => m.suma < base * 1.6);
  const neta = alEuro(mediana(normales.slice(-3).map(([, m]) => m.suma)));

  // Día de cobro: el más repetido en los meses normales (con empate, el último).
  const cuenta = new Map<number, number>();
  for (const [, m] of normales) {
    const dia = Math.max(...m.filas.map(f => Number(f.date.slice(8, 10))));
    cuenta.set(dia, (cuenta.get(dia) ?? 0) + 1);
  }
  const diaHabitual = [...cuenta].sort(
    (a, b) => b[1] - a[1] || b[0] - a[0],
  )[0][0];
  const dia: DiaCobro = diaHabitual >= 28 ? 'ultimo' : diaHabitual;

  // Una paga por mes del año (si se vio dos años, la más reciente manda).
  const porMes = new Map<number, PagaExtra>();
  for (const [mesTexto, m] of extras) {
    const mes = Number(mesTexto.slice(5, 7));
    const extra = alEuro(m.suma - neta);
    // Si llegó en otro ingreso, su día; si no, el de la nómina.
    const aparte =
      m.filas.length > 1
        ? m.filas.reduce((a, b) =>
            Math.abs(a.amount - extra) <= Math.abs(b.amount - extra) ? a : b,
          )
        : null;
    const diaPaga = aparte ? Number(aparte.date.slice(8, 10)) : null;
    porMes.set(mes, {
      id: idPaga(mes),
      nombre: '',
      mes,
      dia: diaPaga == null ? dia : diaPaga >= 28 ? 'ultimo' : diaPaga,
      // Casi igual que la nómina (±10 %): «igual que la nómina».
      importe: Math.abs(extra - neta) <= neta * 0.1 ? null : extra,
    });
  }
  const pagas = [...porMes.values()].sort((a, b) => a.mes - b.mes);
  // Dos pagas en verano (junio y julio) no caben con el mismo nombre.
  const vistos = new Set<string>();
  for (const p of pagas) {
    if (vistos.has(p.id)) {
      p.id = `extra-${p.mes}`;
    }
    vistos.add(p.id);
  }
  const tipo: TipoPagas =
    pagas.length === 0 ? '12' : pagas.length <= 2 ? '14' : '15';
  return { pagador, tipo, neta, dia, pagas };
}

// --- Plan de cada paga -------------------------------------------------------

export type DestinoPaga =
  | { tipo: 'categoria'; id: string; importe: IntegerAmount }
  /** Se queda en «Listo para asignar»: no se mueve nada al repartir. */
  | { tipo: 'por-asignar'; importe: IntegerAmount };

export type EstadoPaga = 'pendiente' | 'repartida' | 'ahora-no';

export type PlanPaga = {
  destinos: DestinoPaga[];
  estado: EstadoPaga;
};

export type PlanesPagas = Record<string, PlanPaga>;

function leerDestino(v: unknown): DestinoPaga | null {
  if (!v || typeof v !== 'object') {
    return null;
  }
  const d = v as Record<string, unknown>;
  if (!esEntero(d.importe) || d.importe <= 0) {
    return null;
  }
  if (d.tipo === 'por-asignar') {
    return { tipo: 'por-asignar', importe: d.importe };
  }
  if (d.tipo === 'categoria' && typeof d.id === 'string' && d.id) {
    return { tipo: 'categoria', id: d.id, importe: d.importe };
  }
  return null;
}

export function parsePlanes(raw: string | null | undefined): PlanesPagas {
  if (!raw) {
    return {};
  }
  let datos: unknown;
  try {
    datos = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!datos || typeof datos !== 'object' || Array.isArray(datos)) {
    return {};
  }
  const planes: PlanesPagas = {};
  for (const [clave, v] of Object.entries(datos)) {
    if (!/^[\w-]+@\d{4}$/.test(clave) || !v || typeof v !== 'object') {
      continue;
    }
    const p = v as Record<string, unknown>;
    planes[clave] = {
      destinos: Array.isArray(p.destinos)
        ? p.destinos
            .map(leerDestino)
            .filter((d): d is DestinoPaga => d !== null)
        : [],
      estado:
        p.estado === 'repartida' || p.estado === 'ahora-no'
          ? p.estado
          : 'pendiente',
    };
  }
  return planes;
}

/** Lo que el plan ya tiene destinado. */
export function totalPlan(plan: PlanPaga | undefined): IntegerAmount {
  return (plan?.destinos ?? []).reduce((s, d) => s + d.importe, 0);
}

/** Lo que queda sin decidir (nunca negativo). */
export function sinDecidir(
  importe: IntegerAmount,
  plan: PlanPaga | undefined,
): IntegerAmount {
  return Math.max(0, importe - totalPlan(plan));
}

/**
 * Pone (o quita, con importe 0) un destino en el plan, sin pasarse de la
 * paga: lo que exceda se recorta. Devuelve un plan nuevo.
 */
export function ponerDestino(
  plan: PlanPaga | undefined,
  destino: DestinoPaga,
  importePagaTotal: IntegerAmount,
): PlanPaga {
  const base: PlanPaga = plan ?? { destinos: [], estado: 'pendiente' };
  const mismo = (d: DestinoPaga) =>
    d.tipo === destino.tipo &&
    (d.tipo === 'por-asignar' ||
      (destino.tipo === 'categoria' && d.id === destino.id));
  const otros = base.destinos.filter(d => !mismo(d));
  const ocupado = otros.reduce((s, d) => s + d.importe, 0);
  const importe = Math.max(
    0,
    Math.min(destino.importe, importePagaTotal - ocupado),
  );
  const destinos = importe > 0 ? [...otros, { ...destino, importe }] : otros;
  return { ...base, destinos };
}

/**
 * Lo que de verdad se puede repartir: los destinos en su orden, recortados a
 * lo que hay en «Por asignar» del mes de la paga (nunca se deja en rojo).
 */
export function recortarReparto(
  destinos: readonly DestinoPaga[],
  disponible: IntegerAmount,
): DestinoPaga[] {
  const recortados: DestinoPaga[] = [];
  let queda = Math.max(0, disponible);
  for (const d of destinos) {
    const importe = Math.min(d.importe, queda);
    if (importe > 0) {
      recortados.push({ ...d, importe });
      queda -= importe;
    }
  }
  return recortados;
}
